<template>
  <div>
    <!-- ========================= SEARCH ========================= -->
    <b-row>
      <b-col
        cols="0"
        md="3"
      />
      <b-col
        cols="12"
        md="6"
      >
        <validation-observer
          ref="dataForm"
          #default="{ invalid }"
        >
          <b-form
            ref="form"
            @submit.stop.prevent="queryTransaction"
          >
            <b-row>
              <b-col cols="12">
                <b-form-group
                  label-for="barcode"
                  label="Scan Ticket/Label"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="barcode"
                    rules=""
                  >
                    <b-input-group class="input-group-merge">
                      <b-input-group-prepend is-text>
                        <feather-icon icon="SearchIcon" />
                      </b-input-group-prepend>

                      <b-form-input
                        id="barcode"
                        v-model.trim="payload.barcode"
                        name="barcode"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Query transactions"
                        autocomplete="off"
                        @input="serverErrors = null"
                      />

                      <b-input-group-append>
                        <b-button
                          type="submit"
                          variant="outline-primary"
                          :disabled="fetching"
                        >
                          <feather-icon
                            v-if="!fetching"
                            icon="TrendingUpIcon"
                          />
                          <b-spinner
                            v-else
                            small
                          />
                          <span
                            v-if="fetching"
                            class="sr-only"
                          >Loading...</span>
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>

                    <small class="text-danger">{{ errors[0] }}</small>
                    <small
                      v-if="serverErrors && serverErrors.barcode"
                      class="text-danger"
                    >
                      {{ serverErrors.barcode[0] }}
                    </small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-col>
      <b-col
        cols="0"
        md="3"
      />
    </b-row>

    <!-- ========================= CURRENT STATUS (TOP) ========================= -->
    <b-row v-if="ticket">
      <b-col
        cols="0"
        md="3"
      />
      <b-col
        cols="12"
        md="6"
      >
        <b-card class="mb-1">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <h5 class="mb-0">
                Current Status
              </h5>
              <small class="text-muted">
                {{ currentStageName }}
                <span v-if="currentTransaction">
                  • {{ currentTransaction.captured_at | moment("DD/MM/YYYY hh:mm A") }}
                </span>
              </small>
            </div>

            <b-badge
              :variant="currentStageBadgeVariant"
              class="text-uppercase"
            >
              {{ currentStageName }}
            </b-badge>
          </div>

          <hr class="my-1">

          <b-row>
            <b-col
              cols="12"
              md="6"
            >
              <p class="mb-50">
                <strong>Ticket:</strong> {{ safeText(ticket.barcode) }}
              </p>
              <p class="mb-50">
                <strong>Tag:</strong> {{ safeText(ticket.link) }}
              </p>
              <p class="mb-50">
                <strong>Mass:</strong> {{ safeText(ticket.mass) }} KG
              </p>
              <p class="mb-50">
                <strong>Sale Number:</strong> {{ safeText(ticket.saleNumber) }}
              </p>
              <p class="mb-50">
                <strong>PCN Number:</strong> {{ safeText(ticket.agreement) }}
              </p>
            </b-col>

            <b-col
              cols="12"
              md="6"
            >
              <p class="mb-50">
                <strong>National Grade:</strong> {{ safeText(ticket.nationalGrade) }}
              </p>
              <p class="mb-50">
                <strong>Company Grade:</strong> {{ safeText(ticket.companyGrade) }}
              </p>
              <p class="mb-50">
                <strong>Factory Grade:</strong> {{ safeText(ticket.factoryGrade) }}
              </p>
              <p class="mb-50">
                <strong>Society:</strong> {{ safeText(ticket.societyName) }}
              </p>
              <p class="mb-50">
                <strong>Market:</strong> {{ safeText(ticket.market) }}
              </p>
            </b-col>
          </b-row>

          <!-- ✅ Location (Road/Warehouse safe) -->
          <b-row
            v-if="ticket.location"
            class="mt-1"
          >
            <b-col cols="12">
              <b-card
                bg-variant="light"
                class="mb-0"
                body-class="py-75"
              >
                <div class="d-flex flex-wrap justify-content-between">
                  <div>
                    <small class="text-muted d-block">Location</small>

                    <div class="font-weight-bolder">
                      {{ locationTitle(ticket.location) }}
                    </div>

                    <div
                      v-if="locationSubTitle(ticket.location)"
                      class="text-muted"
                    >
                      {{ locationSubTitle(ticket.location) }}
                    </div>
                  </div>

                  <!-- Right side: show truck ONLY if road -->
                  <div
                    v-if="isRoadLocation(ticket.location)"
                    class="text-right"
                  >
                    <small class="text-muted d-block">Truck</small>
                    <div class="font-weight-bolder">
                      {{ safeText(ticket.location.truck) }}
                    </div>
                  </div>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <!-- latest actor -->
          <b-row
            v-if="currentTransaction"
            class="mt-1"
          >
            <b-col cols="12">
              <b-media>
                <template #aside>
                  <b-avatar :src="require('@/assets/images/avatars/0.jpg')" />
                </template>

                <h6 class="mb-0">
                  {{ userName(currentTransaction) }}
                </h6>
                <small class="text-muted">
                  {{ userRole(currentTransaction) }}
                  <span v-if="stationName(currentTransaction)"> ({{ stationName(currentTransaction) }})</span>
                </small>

                <div
                  v-if="transportLabel(currentTransaction)"
                  class="text-muted mt-25"
                >
                  Transport: {{ transportLabel(currentTransaction) }}
                </div>
              </b-media>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
      <b-col
        cols="0"
        md="3"
      />
    </b-row>

    <!-- ========================= GROUPED TIMELINE (NEWEST FIRST) ========================= -->
    <b-row v-if="groupedByStage.length">
      <b-col
        cols="0"
        md="3"
      />
      <b-col
        cols="12"
        md="6"
      >
        <b-card
          no-body
          class="mb-0 mt-0"
        >
          <b-card-body>
            <app-timeline>
              <app-timeline-item
                v-for="group in groupedByStage"
                :key="group.stage.id"
                variant="success"
                :icon="group.icon"
              >
                <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1">
                  <div>
                    <h6 class="mb-0">
                      {{ safeText(group.stage && group.stage.name) }}
                      <b-badge
                        v-if="group.count > 1"
                        variant="light-primary"
                        class="ml-50"
                      >
                        {{ group.count }} updates
                      </b-badge>
                    </h6>

                    <small class="text-muted">
                      <span v-if="group.lastAt">Latest: {{ group.lastAt | moment("DD/MM/YYYY hh:mm A") }}</span>
                      <span v-if="group.firstAt"> • First: {{ group.firstAt | moment("DD/MM/YYYY hh:mm A") }}</span>
                    </small>
                  </div>

                  <!-- <div class="text-sm-right mt-50 mt-sm-0">
                    <small class="text-muted d-block">Total in stage</small>
                    <div class="font-weight-bolder">
                      {{ group.totalMass.toFixed(2) }} KG
                    </div>
                  </div> -->
                </div>

                <div class="d-flex flex-sm-row flex-column justify-content-between align-items-start">
                  <b-media>
                    <template #aside>
                      <b-avatar :src="require('@/assets/images/avatars/0.jpg')" />
                    </template>

                    <h6 class="mb-0">
                      {{ safeText(group.latestUser && group.latestUser.name) }}
                    </h6>
                    <small class="text-muted">
                      {{ safeText(group.latestUser && group.latestUser.role) }}
                      <span v-if="group.latestStation && group.latestStation.name">
                        ({{ group.latestStation.name }})
                      </span>
                    </small>

                    <div
                      v-if="group.latestTransport"
                      class="text-muted mt-25"
                    >
                      Transport: {{ safeText(group.latestTransport.number) }} • {{ safeText(group.latestTransport.truck) }}
                    </div>
                  </b-media>
                </div>

                <div
                  v-if="group.count > 1"
                  class="mt-1"
                >
                  <b-button
                    v-b-toggle="collapseId(group.stage.id)"
                    size="sm"
                    variant="outline-primary"
                  >
                    View details
                  </b-button>

                  <b-collapse
                    :id="collapseId(group.stage.id)"
                    class="mt-1"
                  >
                    <b-list-group>
                      <b-list-group-item
                        v-for="(item, idx) in group.items"
                        :key="String(group.stage.id) + '-' + idx"
                        class="d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <div class="font-weight-bolder">
                            {{ item.captured_at | moment("DD/MM/YYYY hh:mm A") }}
                          </div>

                          <small class="text-muted">
                            {{ safeText(item.user && item.user.name) }} • {{ safeText(item.user && item.user.role) }}
                            <span v-if="item.station && item.station.name"> ({{ item.station.name }})</span>
                          </small>

                          <div
                            v-if="item.transport"
                            class="text-muted"
                          >
                            Transport: {{ safeText(item.transport.number) }} • {{ safeText(item.transport.truck) }}
                          </div>
                        </div>

                        <b-badge variant="light-success">
                          {{ displayMass(item) }} KG
                        </b-badge>
                      </b-list-group-item>
                    </b-list-group>
                  </b-collapse>
                </div>

                <div
                  v-else
                  class="mt-1"
                >
                  <b-badge variant="light-success">
                    {{ displayMass(group.items[0]) }} KG
                  </b-badge>
                </div>
              </app-timeline-item>
            </app-timeline>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col
        cols="0"
        md="3"
      />
    </b-row>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BSpinner,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BAvatar,
    BMedia,
    VBToggle,
    BListGroup,
    BListGroupItem,
    BBadge,
    BCollapse,
    VBTooltip,
} from 'bootstrap-vue'

import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import Ripple from 'vue-ripple-directive'
import { ref, computed, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import ticketsStoreModule from '@/views/cromis/sales/tickets/ticketsStoreModule'

export default {
    components: {
        BRow,
        BCol,
        BCard,
        BCardBody,
        BForm,
        BFormGroup,
        BFormInput,
        BButton,
        BSpinner,
        BInputGroup,
        BInputGroupPrepend,
        BInputGroupAppend,
        ValidationProvider,
        ValidationObserver,
        AppTimeline,
        AppTimelineItem,
        BAvatar,
        BMedia,
        BListGroup,
        BListGroupItem,
        BBadge,
        BCollapse,
    },
    directives: { 'b-toggle': VBToggle, 'b-tooltip': VBTooltip, Ripple },

    setup(props, context) {
        const fetching = ref(false)
        const serverErrors = ref(null)

        const ticket = ref(null)
        const transactions = ref([])
        const payload = ref({ barcode: null })

        const CROMIS_STORE_MODULE_NAME = 'cromis-transactions'
        if (!store.hasModule(CROMIS_STORE_MODULE_NAME)) {
            store.registerModule(CROMIS_STORE_MODULE_NAME, ticketsStoreModule)
        }

        onUnmounted(() => {
            if (store.hasModule(CROMIS_STORE_MODULE_NAME)) store.unregisterModule(CROMIS_STORE_MODULE_NAME)
        })

        const safeText = (val, fallback = '—') => (val === null || val === undefined || val === '' ? fallback : val)

        const parseDate = dt => {
            if (!dt) return 0
            return new Date(String(dt).replace(' ', 'T')).getTime()
        }

        // NEWEST FIRST
        const sortedTransactions = computed(() => {
            const list = Array.isArray(transactions.value) ? transactions.value : []
            return [...list].sort((a, b) => parseDate(b.transaction_id) - parseDate(a.transaction_id))
        })

        const currentTransaction = computed(() => {
            const list = sortedTransactions.value
            return list.length ? list[0] : null
        })

        const currentStageName = computed(() => {
            const ct = currentTransaction.value
            return ct && ct.stage && ct.stage.name ? ct.stage.name : '—'
        })

        const currentStageBadgeVariant = computed(() => {
            const ct = currentTransaction.value
            const id = ct && ct.stage ? ct.stage.id : null
            if (id === 1) return 'info'
            if (id === 2) return 'warning'
            if (id === 3) return 'primary'
            return 'secondary'
        })

        const stageIcon = t => {
            const icon = t && t.stage && t.stage.icons ? t.stage.icons.web : null
            if (icon && String(icon).includes('-')) return 'MapPinIcon'
            return icon || 'MapPinIcon'
        }

        // Group by stage, items stay NEWEST FIRST
        const groupedByStage = computed(() => {
            const map = new Map()

            sortedTransactions.value.forEach(t => {
                const key = t && t.stage ? t.stage.id : 'unknown'
                const prev = map.get(key)

                if (!prev) {
                    map.set(key, { stage: t.stage, icon: stageIcon(t), items: [t] })
                } else {
                    prev.items.push(t)
                }
            })

            const groups = Array.from(map.values()).map(group => {
                const { items } = group // already newest-first
                const latest = items[0]
                const first = items[items.length - 1]

                const totalMass = items.reduce((sum, x) => {
                    const m = Number((x && x.transport ? x.transport.mass : x.mass) || 0)
                    return sum + (isNaN(m) ? 0 : m)
                }, 0)

                return {
                    stage: group.stage,
                    icon: group.icon,
                    items,
                    lastAt: latest ? latest.captured_at : null,
                    firstAt: first ? first.captured_at : null,
                    count: items.length,
                    totalMass,
                    latestUser: latest ? latest.user : null,
                    latestStation: latest ? latest.station : null,
                    latestTransport: latest ? latest.transport : null,
                }
            })

            // stage groups NEWEST FIRST
            return groups.sort((a, b) => parseDate(b.lastAt) - parseDate(a.lastAt))
        })

        const displayMass = t => {
            const m = Number((t && t.transport ? t.transport.mass : t && t.mass) || 0)
            return (isNaN(m) ? 0 : m).toFixed(2)
        }

        const collapseId = stageId => `stage-${stageId}`

        const userName = t => safeText(t && t.user && t.user.name)
        const userRole = t => safeText(t && t.user && t.user.role)
        const stationName = t => {
            const name = t && t.station && t.station.name ? t.station.name : null
            return name
        }
        const transportLabel = t => {
            const tr = t && t.transport ? t.transport : null
            if (!tr) return null
            return `${safeText(tr.number)} • ${safeText(tr.truck)}`
        }

        // ✅ Location helpers (Road/Warehouse)
        const isRoadLocation = loc => !!(loc && (loc.from || loc.to || loc.truck || loc.number))
        const isWarehouseLocation = loc => !!(loc && (loc.code || loc.name))

        const locationTitle = loc => {
            if (!loc) return '—'
            const type = safeText(loc.type)
            // Road: "Road • 162600105"
            if (isRoadLocation(loc)) return `${type} • ${safeText(loc.number)}`
            // Warehouse: "Warehouse • T2"
            if (isWarehouseLocation(loc)) return `${type} • ${safeText(loc.code)}`
            return type
        }

        const locationSubTitle = loc => {
            if (!loc) return null
            // Road: "MIEMBA CM → MIEMBA RH"
            if (isRoadLocation(loc) && (loc.from || loc.to)) return `${safeText(loc.from)} → ${safeText(loc.to)}`
            // Warehouse: "EXTTB RH"
            if (isWarehouseLocation(loc) && loc.name) return safeText(loc.name)
            return null
        }

        const queryTransaction = async () => {
            const code = payload.value.barcode
            if (!code) return

            serverErrors.value = null
            ticket.value = null
            transactions.value = []
            fetching.value = true

            try {
                const response = await store.dispatch('cromis-transactions/query', payload.value)
                const t = response && response.data ? response.data.barcode : null
                ticket.value = t
                transactions.value = t && Array.isArray(t.transactions) ? t.transactions : []
            } catch (error) {
                if (error && error.response && error.response.status === 422) {
                    serverErrors.value = error.response.data.errors
                } else {
                    context.root.$swal({
                        icon: 'info',
                        title: 'Barcode Info',
                        text: 'Barcode Information not found, Please check your barcode',
                        showConfirmButton: true,
                        customClass: { confirmButton: 'btn btn-info' },
                        buttonsStyling: false,
                    })
                }
            } finally {
                fetching.value = false
            }
        }

        return {
            fetching,
            serverErrors,
            ticket,
            transactions,
            payload,

            groupedByStage,
            currentTransaction,
            currentStageName,
            currentStageBadgeVariant,

            safeText,
            displayMass,
            collapseId,
            userName,
            userRole,
            stationName,
            transportLabel,

            // location helpers
            isRoadLocation,
            locationTitle,
            locationSubTitle,

            queryTransaction,
        }
    },
}
</script>

<style lang="scss" scoped>
.mb-50 {
  margin-bottom: 0.5rem;
}
</style>
